import { useEffect, useState, useCallback } from "react";
import { useAuth, useNivel4Overline } from "@/context/AuthContext";
import api, { formatError } from "@/lib/api";
import { PageHeader, ScopeNotice, useScopeGuard, useCan } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, BellOff, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const tipoLabel = {
  gaps_ko: "KO abiertos", proveedor_cert: "Certificado proveedor", cliente_cert: "Certificado cliente",
  documento_revision: "Revisión documental", incidencia_vencida: "Incidencia vencida",
  simulacro_retirada: "Simulacro de retirada", sin_documentos: "Sin documentos", cobertura_baja: "Cobertura baja",
};
const SEV = {
  critica: { label: "Crítica", bar: "bg-[#EF4444]", badge: "bg-[#EF4444] hover:bg-[#EF4444]" },
  alta: { label: "Alta", bar: "bg-[#F59E0B]", badge: "bg-[#F59E0B] hover:bg-[#F59E0B]" },
  media: { label: "Media", bar: "bg-[#94A3B8]", badge: "bg-[#94A3B8] hover:bg-[#94A3B8]" },
};

export default function Alertas() {
  const { user, scopeTenant } = useAuth();
  const overlineN4 = useNivel4Overline();
  const canEdit = useCan(user);
  const needsScope = useScopeGuard(user, scopeTenant);
  const [alertas, setAlertas] = useState([]);
  const [abierta, setAbierta] = useState(null);

  const load = useCallback(async () => {
    if (needsScope) return;
    const { data } = await api.get("/alertas");
    setAlertas(data);
  }, [needsScope]);
  useEffect(() => { load(); }, [load]);

  const generar = async () => {
    try {
      const { data } = await api.post("/alertas/generar");
      toast.success(`${data.generadas} alertas generadas`);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  if (needsScope) return <ScopeNotice />;

  return (
    <div data-testid="alertas-page">
      <PageHeader overline={overlineN4} title="Alertas Proactivas"
        action={canEdit && (
          <Button onClick={generar} data-testid="generar-alertas-btn" className="bg-[#FF5625] hover:bg-[#E0451A]">
            <RefreshCw size={16} className="mr-1" /> Regenerar alertas
          </Button>
        )} />

      {alertas.length === 0 ? (
        <Card className="p-12 border-[#E2E8F0] flex flex-col items-center text-[#0E2A47]/50" data-testid="alertas-empty">
          <BellOff size={40} className="mb-3" /> No hay alertas activas.
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {alertas.map((a) => {
            const sev = SEV[a.severidad] || SEV.media;
            const tieneDetalle = (a.detalle || []).length > 0;
            const open = abierta === a.id;
            return (
              <Card key={a.id} className="p-5 border-[#E2E8F0] flex gap-4" data-testid={`alerta-${a.id}`}>
                <span className={`w-1 rounded-full shrink-0 ${sev.bar}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Badge variant="outline" className="text-[10px] border-[#E2E8F0] text-[#0E2A47]/60">{tipoLabel[a.tipo] || a.tipo}</Badge>
                    <Badge className={`text-[10px] ${sev.badge}`} data-testid={`alerta-sev-${a.id}`}>{sev.label}</Badge>
                  </div>
                  <div className="text-sm font-medium text-[#0E2A47]">{a.mensaje}</div>
                  {tieneDetalle && (
                    <>
                      <button onClick={() => setAbierta(open ? null : a.id)} data-testid={`alerta-toggle-${a.id}`}
                        className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#FF5625] hover:underline">
                        {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />} Ver los {a.detalle.length} requisitos
                      </button>
                      {open && (
                        <div className="mt-2 space-y-1.5 border-t border-[#E2E8F0] pt-2" data-testid={`alerta-detalle-${a.id}`}>
                          {a.detalle.map((d, i) => (
                            <div key={i} className="text-xs text-[#0E2A47]/70 flex gap-2">
                              <span className="font-mono font-semibold text-[#EF4444] shrink-0">{d.requisito}</span>
                              <span className="min-w-0">{d.descripcion}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
